import { create } from 'zustand';
import { queryRecords, fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from "../api/fields";
import { findOrCreatePerson, syncPersonFields, availabilityGridToText } from '../lib/people-directory';

const FV = FIELDS.volunteers;
const FVE = FIELDS.volunteerEvents;
const FVA = FIELDS.volunteerAssignments;

// ── Interfaces ─────────────────────────────────────────

export interface Volunteer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  skills: string;
  certifications: string;
  certs: string;          // alias for certifications
  rolePref: string;
  org: string;
  ecName: string;
  ecPhone: string;
  registeredAt: string;
  personId: string;
  availability: string;
  emergencyContact: string;
  emergencyPhone: string;
  status: string;
  notes: string;
  [k: string]: unknown;
}

export interface VolEvent {
  id: number;
  incidentId: number | null;
  eventName: string;
  name: string;           // alias for eventName
  eventDate: string;
  startDate: string;      // alias for eventDate
  endDate: string;        // stored in notes extras
  startTime: string;
  endTime: string;
  location: string;
  address: string;        // stored in notes extras
  description: string;
  requiredSkills: string;
  reqCerts: string;       // stored in notes extras (required certifications)
  maxVolunteers: number;
  targetVol: number;      // alias for maxVolunteers
  type: string;           // stored in notes extras
  checkInCode: string;
  status: string;
  notes: string;
  [k: string]: unknown;
}

export interface VolAssignment {
  id: number;
  eventId: number;
  volunteerId: number;
  volunteerName: string;
  role: string;
  status: string;
  checkInTime: string;
  checkOutTime: string;
  checkIn: string;      // alias for checkInTime
  checkOut: string;     // alias for checkOutTime
  hoursWorked: string;
  shiftDate: string;    // stored in notes extras
  startTime: string;    // stored in notes extras
  endTime: string;      // stored in notes extras
  hours: string;        // stored in notes extras / alias for hoursWorked
  incidentId: string;   // stored in notes extras
  notes: string;
  [k: string]: unknown;
}

// ── Internal helpers (defined before mappers) ──────────

function parseExtrasRaw(notes: string): Record<string, unknown> {
  if (!notes) return {};
  try { return JSON.parse(notes); } catch { return {}; }
}

// ── Mappers ────────────────────────────────────────────

function mapVol(r: Record<string, unknown>): Volunteer {
  const notes = (r[FV.notes] as string) || '';
  const extras = parseExtrasRaw(notes);
  const certifications = (r[FV.certifications] as string) || '';
  return {
    id: r.record_id as number,
    firstName: (r[FV.firstName] as string) || '',
    lastName: (r[FV.lastName] as string) || '',
    email: (r[FV.email] as string) || '',
    phone: (r[FV.phone] as string) || '',
    address: (r[FV.address] as string) || '',
    city: (r[FV.city] as string) || '',
    state: (r[FV.state] as string) || '',
    zip: (r[FV.zip] as string) || '',
    skills: (r[FV.skills] as string) || '',
    certifications,
    certs: certifications,
    rolePref: FV.rolePref ? (r[FV.rolePref] as string) || (extras.rolePref as string) || '' : (extras.rolePref as string) || '',
    org: FV.org ? (r[FV.org] as string) || (extras.org as string) || '' : (extras.org as string) || '',
    ecName: FV.ecName ? (r[FV.ecName] as string) || '' : '',
    ecPhone: FV.ecPhone ? (r[FV.ecPhone] as string) || '' : '',
    registeredAt: FV.registeredAt ? (r[FV.registeredAt] as string) || '' : '',
    personId: FV.personId ? (r[FV.personId] as string) || '' : '',
    availability: (r[FV.availability] as string) || '',
    emergencyContact: (r[FV.emergencyContact] as string) || '',
    emergencyPhone: (r[FV.emergencyPhone] as string) || '',
    status: (r[FV.status] as string) || 'active',
    notes,
  };
}

function mapVolEvent(r: Record<string, unknown>): VolEvent {
  const eventName = (r[FVE.eventName] as string) || '';
  const eventDate = (r[FVE.eventDate] as string) || '';
  const maxVolunteers = Number(r[FVE.maxVolunteers]) || 0;
  const notes = (r[FVE.notes] as string) || '';
  const extras = parseExtrasRaw(notes);
  return {
    id: r.record_id as number,
    incidentId: (r[FVE.incidentId] as number) || null,
    eventName,
    name: eventName,
    eventDate,
    startDate: eventDate,
    endDate: (extras.endDate as string) || '',
    startTime: (r[FVE.startTime] as string) || '',
    endTime: (r[FVE.endTime] as string) || '',
    location: (r[FVE.location] as string) || '',
    address: (extras.address as string) || '',
    description: (r[FVE.description] as string) || '',
    requiredSkills: (r[FVE.requiredSkills] as string) || '',
    reqCerts: (extras.reqCerts as string) || '',
    maxVolunteers,
    targetVol: maxVolunteers,
    type: (extras.type as string) || 'other',
    checkInCode: (r[FVE.checkInCode] as string) || '',
    status: (r[FVE.status] as string) || 'active',
    notes,
  };
}

function mapVolAssign(r: Record<string, unknown>): VolAssignment {
  const notes = (r[FVA.notes] as string) || '';
  const extras = parseExtrasRaw(notes);
  const checkInTime = (r[FVA.checkInTime] as string) || (extras.checkIn as string) || '';
  const checkOutTime = (r[FVA.checkOutTime] as string) || (extras.checkOut as string) || '';
  const hoursWorked = (r[FVA.hoursWorked] as string) || (extras.hours as string) || '';
  return {
    id: r.record_id as number,
    eventId: Number(r[FVA.eventId]) || 0,
    volunteerId: Number(r[FVA.volunteerId]) || 0,
    volunteerName: (r[FVA.volunteerName] as string) || '',
    role: (r[FVA.role] as string) || '',
    status: (r[FVA.status] as string) || 'scheduled',
    checkInTime,
    checkOutTime,
    checkIn: checkInTime,
    checkOut: checkOutTime,
    hoursWorked,
    hours: hoursWorked || (extras.hours as string) || '',
    shiftDate: (extras.shiftDate as string) || '',
    startTime: (extras.startTime as string) || '',
    endTime: (extras.endTime as string) || '',
    incidentId: (extras.incidentId as string) || '',
    personId: FVA.personId ? (r[FVA.personId] as string) || '' : '',
    notes,
  };
}

// ── Field builder helpers ──────────────────────────────

function buildVolFields(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.firstName != null) p[FV.firstName] = data.firstName;
  if (data.lastName != null) p[FV.lastName] = data.lastName;
  if (data.email != null) p[FV.email] = data.email;
  if (data.phone != null) p[FV.phone] = data.phone;
  if (data.address != null) p[FV.address] = data.address;
  if (data.city != null) p[FV.city] = data.city;
  if (data.state != null) p[FV.state] = data.state;
  if (data.zip != null) p[FV.zip] = data.zip;
  if (data.skills != null) p[FV.skills] = data.skills;
  // certs is an alias for certifications
  if (data.certifications != null) p[FV.certifications] = data.certifications;
  else if (data.certs != null) p[FV.certifications] = data.certs;
  if (data.availability != null) p[FV.availability] = data.availability;
  if (data.emergencyContact != null) p[FV.emergencyContact] = data.emergencyContact;
  if (data.emergencyPhone != null) p[FV.emergencyPhone] = data.emergencyPhone;
  if (data.status != null) p[FV.status] = data.status;
  if (data.ecName != null && FV.ecName) p[FV.ecName] = data.ecName;
  if (data.ecPhone != null && FV.ecPhone) p[FV.ecPhone] = data.ecPhone;
  if (data.registeredAt != null && FV.registeredAt) p[FV.registeredAt] = data.registeredAt;
  if (data.personId != null && FV.personId) p[FV.personId] = data.personId;
  if (data.rolePref != null && FV.rolePref) p[FV.rolePref] = data.rolePref;
  if (data.org != null && FV.org) p[FV.org] = data.org;
  // Also keep extras in notes for backward compat with old records that read from notes JSON
  const existingExtras = parseExtrasRaw(data.notes as string || '');
  const extras: Record<string, unknown> = { ...existingExtras };
  let hasExtras = false;
  if (data.rolePref != null) { extras.rolePref = data.rolePref; hasExtras = true; }
  if (data.org != null) { extras.org = data.org; hasExtras = true; }
  if (hasExtras || data.notes != null) {
    p[FV.notes] = hasExtras ? JSON.stringify(extras) : (data.notes as string);
  }
  return p;
}

function buildEventFields(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.incidentId != null) p[FVE.incidentId] = data.incidentId;
  if (data.eventName != null) p[FVE.eventName] = data.eventName;
  else if (data.name != null) p[FVE.eventName] = data.name;
  // startDate is an alias for eventDate
  if (data.eventDate != null) p[FVE.eventDate] = data.eventDate;
  else if (data.startDate != null) p[FVE.eventDate] = data.startDate;
  if (data.startTime != null) p[FVE.startTime] = data.startTime;
  if (data.endTime != null) p[FVE.endTime] = data.endTime;
  if (data.location != null) p[FVE.location] = data.location;
  if (data.description != null) p[FVE.description] = data.description;
  if (data.requiredSkills != null) p[FVE.requiredSkills] = data.requiredSkills;
  // targetVol is an alias for maxVolunteers
  if (data.maxVolunteers != null) p[FVE.maxVolunteers] = data.maxVolunteers;
  else if (data.targetVol != null) p[FVE.maxVolunteers] = data.targetVol;
  if (data.checkInCode != null) p[FVE.checkInCode] = data.checkInCode;
  if (data.status != null) p[FVE.status] = data.status;
  if (data.personId != null && FVE.personId) p[FVE.personId] = data.personId;
  if (data.paProjectId != null && FVE.paProjectId) p[FVE.paProjectId] = data.paProjectId;
  // Merge extras (type, address, endDate, reqCerts) into notes JSON
  const existingExtras = parseExtrasRaw(data.notes as string || '');
  const extras: Record<string, unknown> = { ...existingExtras };
  let hasExtras = false;
  if (data.type != null) { extras.type = data.type; hasExtras = true; }
  if (data.address != null) { extras.address = data.address; hasExtras = true; }
  if (data.endDate != null) { extras.endDate = data.endDate; hasExtras = true; }
  if (data.reqCerts != null) { extras.reqCerts = data.reqCerts; hasExtras = true; }
  if (hasExtras || data.notes != null) {
    p[FVE.notes] = hasExtras ? JSON.stringify(extras) : (data.notes as string);
  }
  return p;
}

function buildAssignFields(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.eventId != null) p[FVA.eventId] = data.eventId;
  if (data.volunteerId != null) p[FVA.volunteerId] = data.volunteerId;
  if (data.volunteerName != null) p[FVA.volunteerName] = data.volunteerName;
  if (data.role != null) p[FVA.role] = data.role;
  if (data.status != null) p[FVA.status] = data.status;
  if (data.checkInTime != null) p[FVA.checkInTime] = data.checkInTime;
  else if (data.checkIn != null) p[FVA.checkInTime] = data.checkIn;
  if (data.checkOutTime != null) p[FVA.checkOutTime] = data.checkOutTime;
  else if (data.checkOut != null) p[FVA.checkOutTime] = data.checkOut;
  if (data.hoursWorked != null) p[FVA.hoursWorked] = data.hoursWorked;
  else if (data.hours != null) p[FVA.hoursWorked] = data.hours;
  if (data.personId != null && FVA.personId) p[FVA.personId] = data.personId;
  // Merge extras (shiftDate, startTime, endTime, incidentId, hours) into notes JSON
  const existingExtras = parseExtrasRaw(data.notes as string || '');
  const extras: Record<string, unknown> = { ...existingExtras };
  if (data.shiftDate != null) extras.shiftDate = data.shiftDate;
  if (data.startTime != null) extras.startTime = data.startTime;
  if (data.endTime != null) extras.endTime = data.endTime;
  if (data.incidentId != null) extras.incidentId = data.incidentId;
  if (data.hours != null) extras.hours = data.hours;
  p[FVA.notes] = JSON.stringify(extras);
  return p;
}

// ── Extras helpers (JSON in notes field) ───────────────

export function parseExtras(notes: string): Record<string, unknown> {
  if (!notes) return {};
  try { return JSON.parse(notes); } catch { return {}; }
}

export function mergeExtras(notes: string, patch: Record<string, unknown>): string {
  const existing = parseExtras(notes);
  return JSON.stringify({ ...existing, ...patch });
}

// ── Store ──────────────────────────────────────────────

interface VolunteerState {
  volunteers: Volunteer[];
  events: VolEvent[];
  assignments: VolAssignment[];
  loading: boolean;
  eventsTotal: number;
  assignmentsTotal: number;

  fetchVolunteers: () => Promise<void>;
  fetchEvents: () => Promise<void>;
  fetchAssignments: (eventId?: number) => Promise<void>;

  createVolunteer: (data: Record<string, unknown>) => Promise<number | null>;
  updateVolunteer: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteVolunteer: (id: number) => Promise<void>;

  createEvent: (data: Record<string, unknown>) => Promise<number | null>;
  updateEvent: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteEvent: (id: number) => Promise<void>;

  createAssignment: (data: Record<string, unknown>) => Promise<number | null>;
  updateAssignment: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteAssignment: (id: number) => Promise<void>;

  // V1 compat aliases
  isLoading: boolean;
  loadVolunteers: () => Promise<void>;
  loadEvents: () => Promise<void>;
  loadAssignments: (eventId?: number) => Promise<void>;
}

export const useVolunteerStore = create<VolunteerState>((set, get) => ({
  volunteers: [], events: [], assignments: [], loading: false,
  eventsTotal: 0, assignmentsTotal: 0,

  // ── Fetch ──────────────────────────────────────────
  fetchVolunteers: async () => {
    try {
      set({ loading: true });
      // Volunteers are global (no incidentId) — fetch all active, capped at 500
      const r = await queryRecords<Record<string, unknown>>(TABLES.volunteers, {
        filters: [{ field: FV.status, operator: 'neq', value: 'inactive' }],
        limit: 500,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ volunteers: r.records.map(mapVol), loading: false });
    } catch (e) { console.error('[Vol]', e); set({ loading: false }); }
  },
  fetchEvents: async () => {
    try {
      const r = await queryRecords<Record<string, unknown>>(TABLES.volunteerEvents, {
        limit: 200,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ events: r.records.map(mapVolEvent), eventsTotal: r.total });
    } catch (e) { console.error('[Vol]', e); }
  },
  fetchAssignments: async (eventId?: number) => {
    try {
      const filters = eventId
        ? [{ field: FVA.eventId, operator: 'eq' as const, value: eventId }]
        : [];
      const r = await queryRecords<Record<string, unknown>>(TABLES.volunteerAssignments, {
        filters,
        limit: 100,
        sort: { field: 'created_at', direction: 'desc' },
      });
      if (eventId) {
        // Merge: replace assignments for this event, keep others
        const existing = get().assignments.filter(a => a.eventId !== eventId);
        set({ assignments: [...existing, ...r.records.map(mapVolAssign)], assignmentsTotal: r.total });
      } else {
        set({ assignments: r.records.map(mapVolAssign), assignmentsTotal: r.total });
      }
    } catch (e) { console.error('[Vol]', e); }
  },

  // ── Volunteers CRUD ────────────────────────────────
  createVolunteer: async (data) => {
    try {
      const personId = await findOrCreatePerson({
        firstName: String(data.firstName ?? ''),
        lastName:  String(data.lastName  ?? ''),
        email:     String(data.email     ?? ''),
        dob:       String((data as Record<string, unknown>).dob ?? ''),
        phone:     String(data.phone     ?? ''),
      });
      if (personId) {
        // Parse extras JSON for fields stored in notes blob (dob, dietary, languages, etc.)
        const extras = parseExtrasRaw(String(data.notes ?? ''));
        await syncPersonFields(personId, {
          email:        String(data.email    ?? ''),
          phone:        String(data.phone    ?? ''),
          ecName:       String(data.ecName   ?? data.emergencyContact ?? extras.ecName  ?? ''),
          ecPhone:      String(data.ecPhone  ?? data.emergencyPhone   ?? extras.ecPhone ?? ''),
          ecRelation:   String((data as Record<string,unknown>).ecRelationship ?? extras.ecRelationship ?? ''),
          skills:       String(data.skills   ?? ''),
          languages:    String((data as Record<string,unknown>).languages ?? extras.languages ?? ''),
          specialNeeds: String((data as Record<string,unknown>).dietary   ?? extras.dietary   ?? ''),
          dob:               String((data as Record<string,unknown>).dob ?? extras.dob ?? ''),
          availabilityNotes: availabilityGridToText(String(data.availability ?? extras.availability ?? '')),
          street:       String(data.address  ?? ''),
          city:         String(data.city     ?? ''),
          state:        String(data.state    ?? ''),
          zip:          String(data.zip      ?? ''),
        });
      }
      const r = await createRecord<Record<string, unknown>>(
        TABLES.volunteers,
        buildVolFields({ ...data, personId }),
      );
      await get().fetchVolunteers();
      return (r.record_id ?? r.id) as number;
    } catch (e) { console.error('[Vol]', e); return null; }
  },
  updateVolunteer: async (id, data) => {
    try {
      await updateRecord(TABLES.volunteers, id, buildVolFields(data));
      await get().fetchVolunteers();
    } catch (e) { console.error('[Vol]', e); }
  },
  deleteVolunteer: async (id) => {
    try {
      await deleteRecord(TABLES.volunteers, id);
      set(s => ({ volunteers: s.volunteers.filter(v => v.id !== id) }));
    } catch (e) { console.error('[Vol]', e); }
  },

  // ── Events CRUD ────────────────────────────────────
  createEvent: async (data) => {
    try {
      const r = await createRecord<Record<string, unknown>>(TABLES.volunteerEvents, buildEventFields(data));
      await get().fetchEvents();
      return (r.record_id ?? r.id) as number;
    } catch (e) { console.error('[Vol]', e); return null; }
  },
  updateEvent: async (id, data) => {
    try {
      await updateRecord(TABLES.volunteerEvents, id, buildEventFields(data));
      await get().fetchEvents();
    } catch (e) { console.error('[Vol]', e); }
  },
  deleteEvent: async (id) => {
    try {
      await deleteRecord(TABLES.volunteerEvents, id);
      set(s => ({ events: s.events.filter(e => e.id !== id) }));
    } catch (e) { console.error('[Vol]', e); }
  },

  // ── Assignments CRUD ───────────────────────────────
  createAssignment: async (data) => {
    try {
      const r = await createRecord<Record<string, unknown>>(TABLES.volunteerAssignments, buildAssignFields(data));
      await get().fetchAssignments();
      return (r.record_id ?? r.id) as number;
    } catch (e) { console.error('[Vol]', e); return null; }
  },
  updateAssignment: async (id, data) => {
    try {
      await updateRecord(TABLES.volunteerAssignments, id, buildAssignFields(data));
      await get().fetchAssignments();
    } catch (e) { console.error('[Vol]', e); }
  },
  deleteAssignment: async (id) => {
    try {
      await deleteRecord(TABLES.volunteerAssignments, id);
      set(s => ({ assignments: s.assignments.filter(a => a.id !== id) }));
    } catch (e) { console.error('[Vol]', e); }
  },

  // V1 compat aliases
  isLoading: false,
  loadVolunteers: async () => { await get().fetchVolunteers(); },
  loadEvents: async () => { await get().fetchEvents(); },
  loadAssignments: async (eventId?: number) => { await get().fetchAssignments(eventId); },
}));
